/**
 * TapToAskSection — pitch + visual mock of the in-app "click a word, AI
 * explains it" feature.
 */
import { Icon, type IconName } from '@/components/Icon';
import { Reveal } from '@/components/Reveal';

interface QA {
  icon: IconName;
  q: string;
  desc: string;
}

const QAS: readonly QA[] = [
  { icon: 'ai',       q: '为什么这里用 in 不用 on？',  desc: '介词搭配上下文秒解释' },
  { icon: 'mistakes', q: '为什么要加 -ed？',          desc: '语法点带你一步步推到根上' },
  { icon: 'hint',     q: 'kindly 在邮件里什么语气？', desc: '不只翻译，更教你用' },
];

export function HomeTapToAsk(): JSX.Element {
  return (
    <section className="relative py-24 md:py-32 border-t border-honey-400/10">
      <div className="max-w-6xl mx-auto px-5 md:px-8 grid gap-12 md:grid-cols-2 items-center">
        <Reveal>
          <div>
            <div className="inline-flex items-center gap-2 text-xs font-arcade tracking-[0.2em] text-mint-300 mb-4">
              <span className="h-px w-6 bg-mint-400/60" />
              TAP-TO-ASK · 选词问老师
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight text-balance text-white">
              选中任意词， <span className="text-gradient-warm">AI 老师 0.3 秒讲明白</span>
            </h2>
            <p className="mt-5 text-lg text-cream-300/90 leading-relaxed">
              不用切到翻译软件，不用翻字典。看不懂哪个词，点一下就行 — 立刻给你
              <span className="text-honey-300"> 词义、词性、为什么这里用它、常见搭配、例句</span>。
              就像旁边真的坐着一位老师。
            </p>
            <ul className="mt-6 space-y-3 text-sm">
              {QAS.map(({ icon, q, desc }) => (
                <li key={q} className="flex gap-3 p-3 rounded-2xl bg-ink-900/40 border border-honey-400/15">
                  <Icon name={icon} size="lg" className="text-honey-300 mt-0.5" />
                  <div>
                    <div className="text-white font-semibold text-sm">{q}</div>
                    <div className="text-xs text-cream-400 mt-0.5">{desc}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <TapToAskMock />
        </Reveal>
      </div>
    </section>
  );
}

function TapToAskMock(): JSX.Element {
  return (
    <div className="glass-card rounded-3xl p-6 relative">
      <div className="text-xs uppercase tracking-[0.2em] font-arcade text-honey-300 mb-4">
        SENTENCE PREVIEW
      </div>
      <div className="text-cream-300 mb-2 text-sm">能否方便分享一下这份报告？</div>
      <div className="font-mono text-lg text-white leading-relaxed mb-4">
        Could you{' '}
        <span className="underline underline-offset-4 decoration-dotted decoration-honey-400 text-honey-300 relative">
          kindly
          <span className="absolute -inset-1 -z-10 bg-honey-400/15 blur-md" />
        </span>{' '}
        share the report?
      </div>
      <div className="rounded-2xl bg-ink-800/80 border border-honey-400/30 p-4 shadow-2xl">
        <div className="flex items-center gap-2 mb-3 text-xs">
          <span className="px-1.5 py-0.5 rounded font-arcade text-honey-300 border border-honey-400/40">
            AI · 速通助教
          </span>
        </div>
        <div className="flex items-baseline gap-2">
          <span className="font-display text-2xl font-extrabold text-honey-300">kindly</span>
          <span className="font-mono text-xs text-cream-400">/ˈkaɪndli/</span>
          <span className="text-xs text-cream-500 uppercase tracking-wider">adv.</span>
        </div>
        <div className="mt-2 text-sm text-cream-100">请；好心地</div>
        <div className="mt-3 rounded-xl bg-honey-500/[0.08] border border-honey-400/20 px-3 py-2 text-sm text-cream-200/95 leading-relaxed inline-flex gap-2">
          <Icon name="hint" size="sm" className="text-honey-300 shrink-0 mt-0.5" />
          <span>在请求句中加 kindly 让语气更得体，常见于商务邮件。</span>
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {['kindly let me know'].map((c) => (
            <span key={c} className="text-xs font-mono px-2 py-0.5 rounded bg-white/[0.05] text-cream-200 border border-white/[0.08]">
              {c}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
